import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import {
  Button,
  Header,
  Textarea,
  Container,
  SpaceBetween,
  Box,
  Grid,
  Link,
  ExpandableSection,
  FileUpload,
  Modal,
  Spinner,
  Checkbox,
  ColumnLayout,
  Tabs
} from "@amzn/awsui-components-react";
import './styles.scss'
import { v4 as uuidv4 } from 'uuid';
import TANCPermissionManager from "./TANCPermissionManager";
import TANCFilterManager from "./TANCFilterManager";
import TANCConfigurationManager from "./TANCConfigurationManager";
import { createPreference, createPreferenceWithExtras, deletePreference, getPreference, listPreference, updatePreference, updatePreferenceWithExtras } from "src/utilities/AppSyncPlusAPIs";
const TANCManager = forwardRef((props, ref) => {
  const [loading, setLoading] = useState(null);
  const [activeTabId, setActiveTabId] = useState('permissions');
  const [configuration, setConfiguration] = useState({});
  const [originalConfiguration, setOriginalConfiguration] = useState(null);
  const [filters, setFilters] = useState([])
  const [originalFilters, setoriginalFilters] = useState([])
  const [filtersObjectForPermissions, setFiltersObjectForPermissions] = useState([])
  const [filterLoadingMessage, setFilterLoadingMessage] = useState(null)
  const [permissionsLoadingMessage, setPermissionsLoadingMessage] = useState(null)
  const [configurationLoadingMessage, setConfigurationLoadingMessage] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [originalPermissions, setOriginalPermissions] = useState(null)
  const [notifyFilters, setNotifyFilters] = useState(false)
  const [notifyPermissions, setNotifyPermissions] = useState(false)
  const [notifyConfigurations, setNotifyConfigurations] = useState(false)
  const memoizedFriscoSchema = useMemo(() => props?.parentInfo?.schema, [JSON.stringify(props?.parentInfo?.schema)]); // created to trigger useeffect only when schema is altered

  useEffect(() => {
    initView()
  }, [])

  useEffect(() => {
    let dropDownObject = []
    dropDownObject.push({ 'label': 'All', 'value': 'All' })
    for (const index in originalFilters) {
      dropDownObject.push({ 'label': originalFilters[index].label, 'value': originalFilters[index].preferenceId })
    }

    dropDownObject.sort((a, b) => {
    if (a.label === 'All') return -1;
    if (b.label === 'All') return 1;
    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
});
    setFiltersObjectForPermissions(dropDownObject)
  }, [originalFilters])

  const initView = () => {
    if (props.tancId) {
      loadFilters()
      loadConfigurations()
      loadPermissions()
    }
  }

  const saveAllConfigs = () => {
    filterSaveNotify({'ACTION': 'SAVE'})
    configurationSaveNotify({'ACTION': 'SAVE'})
    permissionsSaveNotify({'ACTION': 'SAVE'})
  }

  useImperativeHandle(ref, () => ({
    saveAllConfigs
  }));

  function appendConfigurations(existingSchema = [], alteredSchema = []) {
    if (!alteredSchema.length) return [...existingSchema];
    
    const existingMap = new Map(existingSchema.map(item => [item.column_name, item]));
    
    return alteredSchema.map(altered => {
      const existing = existingMap.get(altered.column_name);
      return existing ? { ...existing, data_type: altered.data_type, length: altered.length } : altered;
    });
  }

  function setConfigurationInformation(config) {
    setOriginalConfiguration(config)
    setConfiguration(config)
    setNotifyConfigurations(!notifyConfigurations)
    setConfigurationLoadingMessage(null)
  }

  function loadConfigurations() {
    setConfigurationLoadingMessage({'type':'success', 'message': 'Loading configurations..'})
    listPreference(props.tancId, 'TANC', 'CONFIGURATION', function onSuccess(response) {
      let item = response?.data?.listPreference?.['items']?.[0] || []
      if (item.hasOwnProperty('information')) {
        item['information'] = JSON.parse(item?.['information'])
      } else {
        item['information'] = []
      }
      if (props.parentType == 'FRISCO') {
        // since TANC id does not exist. Keep the configuration info locally. For FRISCO, it must match the FRISCO schema config
        let updatedConfig = appendConfigurations(item?.information || [], memoizedFriscoSchema)
        item['information'] = updatedConfig
      } 
      setConfigurationInformation(item)
      

    }, function onFailure(response) {
    setConfigurationLoadingMessage({'type':'error', 'message': 'Something went wrong!'})
    })

  }

  function loadFilters() {
    setFilterLoadingMessage({'type':'success', 'message': 'Loading all filters..'})
    listPreference(props.tancId, 'TANC', 'FILTER', function onSuccess(response) {
      let items = response?.data?.listPreference?.['items']
      for (let i = 0; i < items?.length; i++) {
        if (items[i].hasOwnProperty('information') || items[i].hasOwnProperty('information') == null || items[i].hasOwnProperty('information') == undefined) {
          items[i]['information'] = JSON.parse(items[i]?.['information'])
          items[i]['extras'] = JSON.parse(items[i]?.['extras'])
        } else {
          items[i]['information'] = []
          items[i]['information'] = {}
        }
      }
      setoriginalFilters(items)
      setFilters(items)
      setNotifyFilters(!notifyFilters)
      setFilterLoadingMessage(null)
    }, function onFailure(response) {
      setFilterLoadingMessage({'type':'error', 'message': 'Something went wrong!'})
    })
  }

  function loadFilterById(filterId, index) {
    setFilterLoadingMessage({'type':'success', 'message': 'Loading filter..'})
    getPreference(filterId, function onSuccess(response) {
      if (filters?.length > 0) {
        filters[index] = response.data.getPreference
        if(filters[index].hasOwnProperty('information')){
          filters[index]['information'] = JSON.parse(filters[index]['information'])
        }
        if(filters[index].hasOwnProperty('extras')){
          filters[index]['extras'] = JSON.parse(filters[index]['extras'])
        }else{
          filters[index]['extras'] = {}
        }
        setFilters(JSON.parse(JSON.stringify(filters)))
      }
      setFilterLoadingMessage(null)
    }, function onFailure(response) {
      //console.log(response, filterId)
      setFilterLoadingMessage({'type':'error', 'message': 'Something went wrong!'})
    })
  }

  function loadPermissions(onResult) {
    setPermissionsLoadingMessage({'type':'success', 'message': 'Loading permissions..'})
    listPreference(props.tancId, 'TANC', 'PERMISSIONS', function onSuccess(response) {
      let item = response?.data?.listPreference?.['items']?.[0]
      if (item) {
        if (item.hasOwnProperty('information')) {
          item['information'] = JSON.parse(item?.['information'])
        } else {
          item['information'] = []
        }
        setOriginalPermissions(item)
        setPermissions(item)
        setNotifyPermissions(!notifyPermissions)
        setPermissionsLoadingMessage(null)
        if(onResult){
          onResult(item)
        }
      }else{
        if(onResult){
          onResult(item)
        }
        setPermissionsLoadingMessage(null)
      }
    }, function onFailure(response) {
      setPermissionsLoadingMessage({'type':'error', 'message': 'Something went wrong!'})
    })
  }

  const updateFilterValue = (updatedConfig) => {
    setFilters(updatedConfig)
  }

  const getFilterValue = () => {
    return filters
  }

  function extractAllFilterIdsFromFilters() {
    let existingIds = []
    for (let i = 0; i < originalFilters?.length; i++) {
      existingIds.push(originalFilters[i].preferenceId)
    }
    return existingIds
  }

  function checkAllFiltersAndCloseLoader() {
    let filterSavePending = false
    for (const filter in filters) {
      if (filter['loading']) {
        filterSavePending = true
        break
      }
    }
    if (!filterSavePending) {
      setFilterLoadingMessage(null)
      loadFilters()
    }

  }

  function deleteFilterIndividual(index){
    let existingIds = extractAllFilterIdsFromFilters()
    if (existingIds.includes(filters[index].preferenceId)) {
      setFilterLoadingMessage({'type':'success', 'message': 'Deleting..'})
      filters[index]['loading'] = true
      deletePreference(filters[index].preferenceId, function onSuccess(response) {
        filters[index]['loading'] = false
        filters.splice(index, 1)
        setFilters(JSON.parse(JSON.stringify(filters)))
        checkAllFiltersAndCloseLoader()
        loadFilters()
      })
    }
    }

  function saveFilterIndividual(index) {
    let existingIds = extractAllFilterIdsFromFilters()
    setFilterLoadingMessage({'type':'success', 'message': 'saving..'})
    filters[index]['loading'] = true
    if (existingIds.includes(filters[index].preferenceId)) {
      updatePreferenceWithExtras(filters[index].preferenceId, filters[index].label, props.userId, filters[index]['information'], filters[index]['extras'], function onSuccess(response) {
        filters[index]['loading'] = false
        setFilters(JSON.parse(JSON.stringify(filters)))
        checkAllFiltersAndCloseLoader()
        loadFilterById(filters[index].preferenceId, index)
      })
    } else {
      let ttl = null
      if (props.tancId.startsWith('TEMP_')) {
        ttl = Math.floor((Date.now() + (24 * 60 * 60 * 1000)) / 1000);
      }
      createPreferenceWithExtras(filters[index].preferenceId, 'FILTER', filters[index].label, props.tancId, 'TANC', props.userId, filters[index]['information'], filters[index]['extras'], ttl, function onSuccess(response) {
        filters[index]['loading'] = false
        setFilters(JSON.parse(JSON.stringify(filters)))
        checkAllFiltersAndCloseLoader()
        loadFilterById(filters[index].preferenceId, index)
      })
    }
  }
  function filterSaveNotifyIndividual(event, index) {
    if (filters) {
      if (event?.['ACTION'] == 'SAVE') {
        saveFilterIndividual(index)
      }
    }
  }

  function filterSaveNotify(event) {
    if (event?.['ACTION'] == 'SAVE') {
      for (let i = 0; i < filters?.length; i++) {
        if(filters[i]['to_delete']){
          deleteFilterIndividual(i)
        }else{
          saveFilterIndividual(i)
        }
      }
    }
  }

  const updateConfigValue = (updatedConfig) => {
    configuration['information'] = updatedConfig
    setConfiguration({ ...configuration })
  }

  const getConfigValue = () => {
    let information = configuration?.['information']
    if (information && Array.isArray(information) && information.some(item => 'column_index' in item)) {
    information.sort((a, b) => a.column_index - b.column_index);
}
    
    return information
  }


  const getConfigValueForDropDowns = () => {
    let information = configuration?.['information']
    if (information && Array.isArray(information) && information.some(item => 'column_index' in item)) {
      information.sort((a, b) => a.column_name.localeCompare(b.column_name));
    }
    let dropDowns = []
    for(const i in information){
      dropDowns.push({'label': information[i].column_name, 'value': information[i].column_name})
    }
    return dropDowns
  }

  const configurationSaveNotify = (event) => {
    if (event?.['ACTION'] == 'SAVE') {
      setPermissionsLoadingMessage({'type':'success', 'message': 'Saving configuration..'})
      setConfiguration(JSON.parse(JSON.stringify(configuration)))
      if (originalConfiguration == null || !originalConfiguration.hasOwnProperty('preferenceId') ) {
        let preferenceId = uuidv4()
        let ttl = null
        if (props.tancId.startsWith('TEMP_')) {
          ttl = Math.floor((Date.now() + (24 * 60 * 60 * 1000)) / 1000);
        }
        createPreference(preferenceId, 'CONFIGURATION', configuration.label, props.tancId, 'TANC', props.userId, configuration['information'], ttl, function onSuccess(response) {
          setPermissionsLoadingMessage(null)
          loadConfigurations()
        })
      } else {
        // save preference
        updatePreference(configuration.preferenceId, configuration.label, props.userId, configuration['information'], function onSuccess(response) {
          setPermissionsLoadingMessage(null)
          loadConfigurations()
        })
      }

    }
  }

  const updatePermissionsValue = (updatedConfig) => {
    if(permissions){
      permissions['information'] = updatedConfig
    setPermissions({ ...permissions })
    }
  }

  const getPermissionsValue = () => {
    return permissions?.['information']
  }

  const permissionsSaveNotify = (event) => {
    if (event?.['ACTION'] == 'SAVE') {
      // save preference
      permissions['loading'] = true
      setPermissionsLoadingMessage({'type':'success', 'message': 'saving permissions..'})
      setPermissions(JSON.parse(JSON.stringify(permissions)))
      if (originalPermissions == null || !originalPermissions.hasOwnProperty('preferenceId')) {
        let preferenceId = uuidv4()
        let ttl = null
        if (props.tancId.startsWith('TEMP_')) {
          ttl = Math.floor((Date.now() + (24 * 60 * 60 * 1000)) / 1000);
        }
        createPreference(preferenceId, 'PERMISSIONS', permissions.label, props.tancId, 'TANC', props.userId, permissions['information'], ttl, function onSuccess(response) {
          setPermissionsLoadingMessage(null)
          loadPermissions()
        })
      } else {
        updatePreference(permissions.preferenceId, permissions.label, props.userId, permissions['information'], function onSuccess(response) {
          setPermissionsLoadingMessage(null)
          loadPermissions()
        })
      }
    }
  }

  const checkIfFilterIsUsed = (preferenceId, onCheckResult) => {
    loadPermissions(function onResult(response) {
      let result = false
      if (response) {
        let permission = response?.['information']
        for (let i = 0; i < permission?.length; i++) {
          if (permission[i]['filter'] == preferenceId) {
            result = true
            break
          }
        }
      }
      onCheckResult({ 'isFilterUsed': result })
    })
  }

  return (
    <Box>
      {
        loading ? <>  <span className="grey_text"> <Spinner></Spinner> {loading}</span></> : <>
          <Tabs
            onChange={({ detail }) =>
              setActiveTabId(detail.activeTabId)
            }
            activeTabId={activeTabId}
            tabs={[
              {
                label: "Permissions",
                id: "permissions",
                content: <TANCPermissionManager
                  tancId={props.tancId} associationType={props.parentType} userId={props.userId} getValues={getPermissionsValue} updateValue={updatePermissionsValue}
                  typeKey={'type'} aliasKey={'alias'} roleKey={'role'} filterKey={'filter'} filters={filtersObjectForPermissions}
                  notify={permissionsSaveNotify} loadingMessage={permissionsLoadingMessage} reloadNotifiers={notifyPermissions}
                ></TANCPermissionManager>
              },
              {
                label: "Filters",
                id: "filters",
                content: <TANCFilterManager
                  tancId={props.tancId} userId={props.userId} 
                  getValues={getFilterValue} updateValue={updateFilterValue} getColumns={getConfigValueForDropDowns}
                  columnNameKey={'column_name'} valuesKey={'values'} radioKey={'can_edit'}
                  lastUpdatedTimeKey={'lastUpdatedTimestamp'}
                  selectHint="Choose data type"
                  notify={filterSaveNotify}
                  notifyIndividual={filterSaveNotifyIndividual}
                  checkIfFilterIsUsed={checkIfFilterIsUsed}
                  placeholder="column name" filterNameKey="label" filterConfigsKey="information" 
                  loadingMessage={filterLoadingMessage} reloadNotifiers={notifyFilters}></TANCFilterManager>
              },
              {
                label: "Configurations",
                id: "configurations",
                content: <TANCConfigurationManager tancId={props.tancId} associationType={props.parentType} userId={props.userId} 
                getValues={getConfigValue} updateValue={updateConfigValue}
                  columnNameKey={'column_name'} dataTypeKey={'data_type'} editTypeKey={'edit_type'} radioKey={'mandatory'}
                  scaleKey={'scale'} precisionKey={'precision'} lengthKey={'length'} lastUpdatedTimeKey={'last_updated_time_stamp'}
                  selectHint="Choose data type" radioText="Mandatory" radioKeyVisible={false} radioKeyDefault={true}
                  notify={configurationSaveNotify}
                  placeholder="column name" loadingMessage={configurationLoadingMessage} reloadNotifiers={notifyConfigurations}
                ></TANCConfigurationManager>
              }
            ]}
          />
        </>
      }
    </Box>

  )
}
)
export default TANCManager;